import jsPDF from "jspdf";
import { logoBase64Invoice } from "../../../shared/utils/constants";

// Opens an A4 PDF invoice for an order (the trackOrder response) in a new browser tab.
export const openInvoicePdf = (response) => {
  // Initialize jsPDF with A4 dimensions (portrait)
  const doc = new jsPDF("p", "mm", "a4");
  const pageHeight = doc.internal.pageSize.getHeight();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 20;
  let y = margin;

  // Helper: Check if there is enough space; if not, add a new page.
  const checkPageBreak = (neededSpace = 10) => {
    if (y + neededSpace > pageHeight - margin) {
      doc.addPage();
      y = margin;
    }
  };

  // === Invoice Header ===
  doc.setFontSize(16);
  doc.setFont("helvetica", "bold");
  checkPageBreak(15);
  // Move the logo to the top-right corner.
  const logoWidth = 50;
  const logoHeight = 15;
  const logoX = pageWidth - margin - logoWidth;
  doc.addImage(logoBase64Invoice, "PNG", logoX, y, logoWidth, logoHeight);
  // Place the "Invoice" title at the left.
  doc.text("Invoice", margin, y + logoHeight);
  y += logoHeight + 10;

  // === Order Basic Details ===
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  checkPageBreak(30);
  doc.text(`Order Number: ${response.orderNumber}`, margin, y);
  y += 6;
  doc.text(
    `Date: ${new Date(response.createdAt).toLocaleDateString()}`,
    margin,
    y
  );
  y += 6;
  doc.text(`Payment Method: ${response.paymentMethod}`, margin, y);
  y += 6;
  doc.text(`Status: ${response.status}`, margin, y);
  y += 10;

  // === Billing and Shipping Information (Side by Side) ===
  checkPageBreak(40);
  // Calculate column widths for two side-by-side blocks.
  const columnWidth = (pageWidth - 2 * margin) / 2;
  const billingX = margin;
  const shippingX = margin + columnWidth;
  let billY = y;
  let shipY = y;

  // Headers for each column.
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  doc.text("Billing Information", billingX, billY);
  doc.text("Shipping Information", shippingX, shipY);
  billY += 6;
  shipY += 6;

  // Billing details.
  doc.setFontSize(10);
  doc.setFont("helvetica", "normal");
  doc.text(
    `Name: ${response.billingFirstName} ${response.billingLastName}`,
    billingX,
    billY
  );
  billY += 6;
  doc.text(`Email: ${response.billingEmail}`, billingX, billY);
  billY += 6;
  doc.text(`Phone: ${response.billingPhone}`, billingX, billY);
  billY += 6;
  const billingAddress = `Address: ${response.billingAddress1}, ${response.billingCity}, ${response.billingState}, ${response.billingPostcode}, ${response.billingCountry}`;
  const billingAddressLines = doc.splitTextToSize(
    billingAddress,
    columnWidth
  );
  billingAddressLines.forEach((line) => {
    checkPageBreak(6);
    doc.text(line, billingX, billY);
    billY += 6;
  });

  // Shipping details.
  doc.text(
    `Name: ${response.shippingFirstName} ${response.shippingLastName}`,
    shippingX,
    shipY
  );
  shipY += 6;
  doc.text(`Email: ${response.shippingEmail}`, shippingX, shipY);
  shipY += 6;
  doc.text(`Phone: ${response.shippingPhone}`, shippingX, shipY);
  shipY += 6;
  const shippingAddress = `Address: ${response.shippingAddress1}, ${response.shippingCity}, ${response.shippingState}, ${response.shippingPostcode}, ${response.shippingCountry}`;
  const shippingAddressLines = doc.splitTextToSize(
    shippingAddress,
    columnWidth
  );
  shippingAddressLines.forEach((line) => {
    checkPageBreak(6);
    doc.text(line, shippingX, shipY);
    shipY += 6;
  });

  // Move y to the lower end of the two columns.
  y = Math.max(billY, shipY) + 4;

  // === Order Items Table with Borders ===
  checkPageBreak(20);
  doc.setFontSize(12);
  doc.setFont("helvetica", "bold");
  const tableX = margin;
  let tableY = y;
  const tableWidth = pageWidth - 2 * margin;
  const col1Width = tableWidth * 0.6; // Name column
  const col2Width = tableWidth * 0.2; // Quantity column

  // Draw table header with borders.
  const headerHeight = 6;
  // Header texts with a bit of padding.
  doc.text("Name", tableX + 2, tableY + 4);
  doc.text("Quantity", tableX + col1Width + 2, tableY + 4);
  doc.text("Price", tableX + col1Width + col2Width + 2, tableY + 4);
  // Draw header rectangle border.
  doc.rect(tableX, tableY, tableWidth, headerHeight);
  // Draw vertical lines in header.
  doc.line(
    tableX + col1Width,
    tableY,
    tableX + col1Width,
    tableY + headerHeight
  );
  doc.line(
    tableX + col1Width + col2Width,
    tableY,
    tableX + col1Width + col2Width,
    tableY + headerHeight
  );
  tableY += headerHeight;

  doc.setFont("helvetica", "normal");
  // Render each product as a table row with borders.
  response.products.forEach((product) => {
    // Wrap the product name if needed, subtracting padding.
    const nameLines = doc.splitTextToSize(
      product.productName,
      col1Width - 4
    );
    const rowHeight = Math.max(nameLines.length * 6, 6) + 2; // adding vertical padding
    checkPageBreak(rowHeight);
    const rowY = tableY;

    // Draw cell border for the row.
    doc.rect(tableX, rowY, tableWidth, rowHeight);
    // Draw vertical dividing lines.
    doc.line(
      tableX + col1Width,
      rowY,
      tableX + col1Width,
      rowY + rowHeight
    );
    doc.line(
      tableX + col1Width + col2Width,
      rowY,
      tableX + col1Width + col2Width,
      rowY + rowHeight
    );

    // Print product name (with padding).
    let currentLineY = rowY + 4;
    nameLines.forEach((line) => {
      doc.text(line, tableX + 2, currentLineY);
      currentLineY += 6;
    });
    // Print quantity and price in their cells.
    doc.text(product.quantity.toString(), tableX + col1Width + 2, rowY + 4);
    doc.text(
      "LKR " + product.price.toFixed(2),
      tableX + col1Width + col2Width + 2,
      rowY + 4
    );

    tableY += rowHeight;
  });
  y = tableY + 4;

  // Define two X positions near the right side for label & price
  const summaryLabelX = pageWidth - margin - 50; // Adjust "50" as needed
  const summaryPriceX = pageWidth - margin;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);

  // Subtotal
  doc.text("Subtotal:", summaryLabelX, y, { align: "right" });
  doc.text(`LKR ${response.totalAmount.toFixed(2)}`, summaryPriceX, y, {
    align: "right",
  });
  y += 6;

  // Delivery Fee
  doc.text("Delivery Fee:", summaryLabelX, y, { align: "right" });
  doc.text(`LKR ${response.deliveryFee.toFixed(2)}`, summaryPriceX, y, {
    align: "right",
  });
  y += 6;

  // Discount
  doc.text("Discount:", summaryLabelX, y, { align: "right" });
  doc.text(`LKR ${response.orderDiscount.toFixed(2)}`, summaryPriceX, y, {
    align: "right",
  });
  y += 6;

  // Tax
  doc.text("Tax:", summaryLabelX, y, { align: "right" });
  doc.text(`LKR ${response.orderTax.toFixed(2)}`, summaryPriceX, y, {
    align: "right",
  });
  y += 6;

  // Bold the Total row
  doc.setFont("helvetica", "bold");
  doc.text("Total:", summaryLabelX, y, { align: "right" });
  doc.text(`LKR ${response.netAmount.toFixed(2)}`, summaryPriceX, y, {
    align: "right",
  });
  doc.setFont("helvetica", "normal");
  y += 6;

  // Draw a horizontal line separator
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 6;

  // Footer Section (printed right below the summary)
  doc.text("https://bigmall.lk/", margin, y);
  y += 6;
  doc.text("Email: info@bigmall.lk", margin, y);
  y += 6;
  doc.text("Tel: (+94) 777961772 / (+94) 741337427", margin, y);

  doc.output("dataurlnewwindow");
};
